import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, CheckCircle, AlertCircle, ChevronRight, ChevronLeft, BedDouble, UserCheck, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import apiClient from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

const emptyLead = () => ({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', docType: 'NID', docNumber: '', docExpiry: '' });

const BookingModal = ({ isOpen, onClose, roomData = null }) => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [bookForSelf, setBookForSelf] = useState(true);
  const [numberOfRooms, setNumberOfRooms] = useState(1);
  const [guests, setGuests] = useState(1);
  const [lead, setLead] = useState(emptyLead());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [apiError, setApiError] = useState('');

  const checkIn = roomData?.prefilledData?.checkIn || '';
  const checkOut = roomData?.prefilledData?.checkOut || '';
  const nights = checkIn && checkOut
    ? Math.max(1, Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000))
    : 1;

  const price = roomData?.price || 0;
  const taxPercent = roomData?.taxPercent ?? 10;
  const serviceChargePercent = roomData?.serviceChargePercent ?? 0;
  const subTotal = price * nights * numberOfRooms;
  const taxAmount = Math.round(subTotal * (taxPercent / 100) * 100) / 100;
  const serviceCharge = Math.round(subTotal * (serviceChargePercent / 100) * 100) / 100;
  const grandTotal = subTotal + taxAmount + serviceCharge;

  const maxRooms = roomData?.availableUnits || 1;
  const maxCapacity = roomData?.occupancy || 0;

  const prefillFromUser = () => {
    if (!user) return emptyLead();
    const parts = (user.name || '').trim().split(/\s+/);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      email: user.userId || '',
      phone: '',
      address: '',
      city: '',
      docType: 'NID',
      docNumber: '',
      docExpiry: '',
    };
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(1);
      setResult(null);
      setApiError('');
      setNumberOfRooms(1);
      setGuests(roomData?.prefilledData?.guests || 1);
      setBookForSelf(true);
      setLead(prefillFromUser());
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roomData]);

  const handleBookingTypeChange = (forSelf) => {
    setBookForSelf(forSelf);
    setLead(forSelf ? prefillFromUser() : emptyLead());
  };

  const step1Valid = () =>
    lead.firstName && lead.lastName && lead.email && lead.phone &&
    lead.address && lead.city && lead.docNumber &&
    (lead.docType !== 'PASSPORT' || lead.docExpiry);

  const handleBook = async () => {
    if (!isLoggedIn) { navigate('/login', { state: { from: '/rooms' } }); onClose(); return; }
    setSubmitting(true);
    setApiError('');
    try {
      const passenger = {
        type: 0,
        isLeadPassenger: true,
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phoneNumber: lead.phone,
        address: lead.address,
        city: lead.city,
        document: {
          documentType: lead.docType,
          documentNumber: lead.docNumber,
          expiryDate: lead.docExpiry || null,
        },
      };

      const data = await apiClient.post('/api/Book/BookRequest', {
        roomTypeId: roomData.roomTypeId,
        numberOfRooms,
        aDT: guests,
        cHD: 0,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        passengers: [passenger],
      });
      setResult(data);
      setStep(3);
    } catch (err) {
      setApiError(err.message || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 relative flex-shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center">
            <X size={16} />
          </button>
          <h2 className="text-xl font-bold">{step === 3 ? 'Booking Confirmed!' : 'Book Your Room'}</h2>
          <p className="text-blue-100 text-sm mt-0.5">{roomData?.name}</p>
          {step < 3 && (
            <div className="flex items-center gap-2 mt-3">
              {[1, 2].map(s => (
                <div key={s} className="flex items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-white text-blue-600' : 'bg-blue-800 text-blue-300'}`}>{s}</div>
                  {s < 2 && <div className={`w-12 h-0.5 mx-1 ${step > s ? 'bg-white' : 'bg-blue-800'}`} />}
                </div>
              ))}
              <span className="text-blue-100 text-xs ml-2">{step === 1 ? 'Guest Details' : 'Review & Confirm'}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* Step 1: Guest Details */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Dates + capacity info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 bg-blue-50 rounded-lg px-4 py-3">
                <span className="flex items-center gap-1.5"><Calendar size={15} className="text-blue-600" />{new Date(checkIn).toLocaleDateString()} → {new Date(checkOut).toLocaleDateString()}</span>
                <span className="text-gray-400">·</span>
                <span>{nights} night{nights !== 1 ? 's' : ''}</span>
                {maxCapacity > 0 && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className="flex items-center gap-1"><Users size={13} className="text-blue-600" /> Max {maxCapacity} guest{maxCapacity !== 1 ? 's' : ''} per room</span>
                  </>
                )}
              </div>

              {/* Number of rooms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <BedDouble size={15} className="text-blue-600" /> Number of Rooms
                  <span className="ml-1 text-xs text-gray-400">({maxRooms} available)</span>
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setNumberOfRooms(n => Math.max(1, n - 1))}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 disabled:opacity-40"
                    disabled={numberOfRooms <= 1}
                  >−</button>
                  <span className="text-xl font-bold w-8 text-center">{numberOfRooms}</span>
                  <button
                    onClick={() => setNumberOfRooms(n => Math.min(maxRooms, n + 1))}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 disabled:opacity-40"
                    disabled={numberOfRooms >= maxRooms}
                  >+</button>
                  <span className="text-sm text-gray-500">× ৳{price.toLocaleString()} × {nights} night{nights !== 1 ? 's' : ''} = <strong>৳{subTotal.toLocaleString()}</strong></span>
                </div>
              </div>

              {/* Number of guests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Users size={15} className="text-blue-600" /> Number of Guests
                  {maxCapacity > 0 && <span className="ml-1 text-xs text-gray-400">(max {maxCapacity} per room)</span>}
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setGuests(n => Math.max(1, n - 1))}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 disabled:opacity-40"
                    disabled={guests <= 1}
                  >−</button>
                  <span className="text-xl font-bold w-8 text-center">{guests}</span>
                  <button
                    onClick={() => setGuests(n => Math.min(maxCapacity * numberOfRooms || 10, n + 1))}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 disabled:opacity-40"
                    disabled={maxCapacity > 0 && guests >= maxCapacity * numberOfRooms}
                  >+</button>
                  <span className="text-sm text-gray-500">guest{guests !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Book for self or others */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Booking for</label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${bookForSelf ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" className="sr-only" checked={bookForSelf} onChange={() => handleBookingTypeChange(true)} />
                    <UserCheck size={18} className={bookForSelf ? 'text-blue-600' : 'text-gray-400'} />
                    <div>
                      <div className="text-sm font-medium">Myself</div>
                      <div className="text-xs text-gray-500">Pre-fill my details</div>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${!bookForSelf ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" className="sr-only" checked={!bookForSelf} onChange={() => handleBookingTypeChange(false)} />
                    <UserPlus size={18} className={!bookForSelf ? 'text-blue-600' : 'text-gray-400'} />
                    <div>
                      <div className="text-sm font-medium">Someone Else</div>
                      <div className="text-xs text-gray-500">Enter guest details</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Lead guest form */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">
                  <Users size={15} className="text-blue-600" /> Guest Details
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="First Name" value={lead.firstName} onChange={v => setLead(l => ({ ...l, firstName: v }))} />
                  <Input label="Last Name" value={lead.lastName} onChange={v => setLead(l => ({ ...l, lastName: v }))} />
                  <Input label="Email" type="email" value={lead.email} onChange={v => setLead(l => ({ ...l, email: v }))} />
                  <Input label="Phone" type="tel" value={lead.phone} onChange={v => setLead(l => ({ ...l, phone: v }))} />
                  <Input label="Address" value={lead.address} onChange={v => setLead(l => ({ ...l, address: v }))} />
                  <Input label="City" value={lead.city} onChange={v => setLead(l => ({ ...l, city: v }))} />
                </div>
                <DocFields
                  docType={lead.docType} docNumber={lead.docNumber} docExpiry={lead.docExpiry}
                  onType={v => setLead(l => ({ ...l, docType: v }))}
                  onNumber={v => setLead(l => ({ ...l, docNumber: v }))}
                  onExpiry={v => setLead(l => ({ ...l, docExpiry: v }))}
                />
              </div>

              {apiError && <ErrorBox msg={apiError} />}

              <div className="flex justify-end pt-2">
                <Button onClick={() => setStep(2)} disabled={!step1Valid()} className="flex items-center gap-2">
                  Review Booking <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Review & Confirm */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Booking summary */}
              <Card className="p-4 bg-gray-50">
                <h4 className="font-semibold mb-3 text-gray-800">Booking Summary</h4>
                <div className="text-sm space-y-2 text-gray-700">
                  <Row label="Room" value={roomData?.name} />
                  <Row label="Rooms" value={`${numberOfRooms} room${numberOfRooms !== 1 ? 's' : ''}`} />
                  <Row label="Guests" value={`${guests} guest${guests !== 1 ? 's' : ''}`} />
                  <Row label="Check-in" value={new Date(checkIn).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
                  <Row label="Check-out" value={new Date(checkOut).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
                  <Row label="Nights" value={nights} />
                </div>
              </Card>

              {/* Guest summary */}
              <Card className="p-4 bg-gray-50">
                <h4 className="font-semibold mb-3 text-gray-800">Guest Details</h4>
                <div className="text-sm space-y-2 text-gray-700">
                  <Row label="Name" value={`${lead.firstName} ${lead.lastName}`} />
                  <Row label="Email" value={lead.email} />
                  <Row label="Phone" value={lead.phone} />
                  <Row label="Address" value={`${lead.address}, ${lead.city}`} />
                  <Row label="Document" value={`${lead.docType}: ${lead.docNumber}`} />
                </div>
              </Card>

              {/* Price breakdown */}
              <Card className="p-4">
                <h4 className="font-semibold mb-3 text-gray-800">Price Breakdown</h4>
                <div className="text-sm space-y-2">
                  <Row
                    label={`৳${price.toLocaleString()} × ${nights} night${nights !== 1 ? 's' : ''} × ${numberOfRooms} room${numberOfRooms !== 1 ? 's' : ''}`}
                    value={`৳${subTotal.toLocaleString()}`}
                  />
                  <Row label={`Tax (${taxPercent}%)`} value={`৳${taxAmount.toLocaleString()}`} />
                  {serviceChargePercent > 0 && (
                    <Row label={`Service Charge (${serviceChargePercent}%)`} value={`৳${serviceCharge.toLocaleString()}`} />
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-base mt-2">
                    <span>Total</span>
                    <span className="text-blue-600">৳{grandTotal.toLocaleString()} BDT</span>
                  </div>
                </div>
              </Card>

              {apiError && <ErrorBox msg={apiError} />}

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex items-center gap-2">
                  <ChevronLeft size={16} /> Back
                </Button>
                <Button onClick={handleBook} disabled={submitting} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && result && (
            <div className="text-center space-y-5 py-4">
              <CheckCircle size={56} className="mx-auto text-green-500" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Booking Confirmed!</h3>
                <p className="text-gray-500 mt-1">Your room is on hold for 15 minutes.</p>
              </div>

              <Card className="p-5 text-left bg-green-50 border-green-200">
                <div className="text-sm space-y-2">
                  <Row label="PNR" value={<span className="font-mono font-bold text-green-700">{result.pnr}</span>} />
                  <Row label="Room" value={result.roomTypeName} />
                  <Row label="Rooms" value={`${result.numberOfRooms ?? numberOfRooms} room${(result.numberOfRooms ?? numberOfRooms) !== 1 ? 's' : ''}`} />
                  <Row label="Guests" value={`${result.adults ?? guests} guest${(result.adults ?? guests) !== 1 ? 's' : ''}`} />
                  <Row label="Check-in" value={new Date(result.checkInDate).toLocaleDateString()} />
                  <Row label="Check-out" value={new Date(result.checkOutDate).toLocaleDateString()} />
                  <Row label="Nights" value={result.nights} />
                  <div className="border-t pt-2 mt-1 space-y-1">
                    <Row label={`Tax (${taxPercent}%)`} value={`৳${Number(result.taxAmount).toLocaleString()}`} />
                    {serviceChargePercent > 0 && (
                      <Row label={`Service Charge (${serviceChargePercent}%)`} value={`৳${Number(result.serviceChargeAmount).toLocaleString()}`} />
                    )}
                    <Row label="Grand Total" value={<span className="font-bold text-blue-700">৳{Number(result.grandTotal).toLocaleString()} {result.currency}</span>} />
                  </div>
                  <Row label="Hold Expires" value={<span className="text-yellow-700">{new Date(result.holdExpiresAt).toLocaleString()}</span>} />
                </div>
              </Card>

              <p className="text-sm text-gray-500">Go to <strong>My Bookings</strong> to manage your reservation.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={onClose} variant="outline">Close</Button>
                <Button onClick={() => { onClose(); navigate('/my-bookings'); }}>View My Bookings</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label} *</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} required
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
  </div>
);

const DocFields = ({ docType, docNumber, docExpiry, onType, onNumber, onExpiry }) => (
  <div className="grid grid-cols-2 gap-3 mt-3">
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Document Type *</label>
      <select value={docType} onChange={e => onType(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
        <option value="NID">NID</option>
        <option value="PASSPORT">Passport</option>
      </select>
    </div>
    <Input label={docType === 'NID' ? 'NID Number' : 'Passport Number'} value={docNumber} onChange={onNumber} />
    {docType === 'PASSPORT' && (
      <div className="col-span-2">
        <Input label="Passport Expiry Date" value={docExpiry} onChange={onExpiry} type="date" />
      </div>
    )}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-right">{value}</span>
  </div>
);

const ErrorBox = ({ msg }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
    <span>{msg}</span>
  </div>
);

export default BookingModal;
